/**
 * One spec's weekly trend: the average as a line, the week's min–max range as a
 * shaded band behind it, with n (that spec's sample count) in the tooltip.
 * Clicking anywhere in a week's band selects it.
 *
 * Follows the RFT rate chart's conventions — the carried `active` object, the
 * `num()` template wrapper, markers only where they carry meaning, the line
 * broken across weeks with no data — but plots an arbitrary value domain rather
 * than 0–100%. The axis window is fitted to the observed min–max range with
 * padding, on a "nice" step: these specs move in narrow bands (Brix roughly
 * 10.4–13.7 across two and a half years), and a zero-based axis would flatten
 * every real movement into a straight line.
 *
 * A week where the spec has no values (n=0) is a gap in both the line and the
 * band, never a zero — a zero here would plot as a catastrophic spec excursion,
 * which is precisely the misreading the backend's null exclusion exists to
 * prevent.
 */

import { useState } from 'react'

import { useElementWidth } from '../shared/useElementWidth'

import { fmtSpec, weekLabel, weekShort } from './format'

const HEIGHT = 220
const PAD = { top: 18, right: 16, bottom: 34, left: 56 }

export interface WeekStat {
  week: string
  /** Samples with a value for this spec that week. */
  n: number
  /** Null when the week has no values for this spec — a gap, not a zero. */
  avg: number | null
  min: number | null
  max: number | null
  /**
   * The week's spec band and target, when every sample in the week agrees on
   * them — null when mixed (products with different limits under an
   * all-products view) or undefined for the spec, and nothing is drawn rather
   * than a wrong line. A spec revision appears as a step at the week it
   * changed.
   */
  lo: number | null
  hi: number | null
  tgt: number | null
  /** Samples outside their own row's band this week. */
  oos: number
}

interface Props {
  data: readonly WeekStat[]
  /** Display precision for values and ticks. */
  decimals: number
  selectedWeek: string | null
  onSelectWeek: (week: string) => void
  emptyLabel?: string
}

/** Coordinates are numbers; `restrict-template-expressions` refuses to stringify
 *  them implicitly inside a path template, and this is pure noise otherwise. */
function num(value: number): string {
  return value.toString()
}

/** The 1/2/5 ladder: a rough step onto the nearest readable one. */
function niceStep(rough: number): number {
  const pow = Math.pow(10, Math.floor(Math.log10(rough)))
  const unit = rough / pow
  const factor = unit <= 1 ? 1 : unit <= 2 ? 2 : unit <= 5 ? 5 : 10
  return factor * pow
}

/** A tick label without trailing zeros beyond the panel's precision. */
function tickLabel(value: number, decimals: number): string {
  return Number(value.toFixed(decimals)).toString()
}

interface Segment {
  week: string
  avg: number
  min: number
  max: number
  i: number
}

export function SpecTrendChart({
  data,
  decimals,
  selectedWeek,
  onSelectWeek,
  emptyLabel = 'No samples for this spec in the current view.',
}: Props) {
  const { ref, width } = useElementWidth<HTMLDivElement>()
  const [hovered, setHovered] = useState<number | null>(null)

  const valued = data.filter((d) => d.avg !== null)
  if (valued.length === 0) return <div className="state">{emptyLabel}</div>

  const chartWidth = Math.max(width, 320)
  const plotWidth = chartWidth - PAD.left - PAD.right
  const plotHeight = HEIGHT - PAD.top - PAD.bottom

  // The axis window: the observed min–max envelope — widened to include the
  // spec band and target where present, so a limit line can never sit off the
  // top of the chart while the data quietly approaches it.
  const lows = valued.map((d) => d.min as number)
  const highs = valued.map((d) => d.max as number)
  for (const d of data) {
    for (const bound of [d.lo, d.hi, d.tgt]) {
      if (bound !== null) {
        lows.push(bound)
        highs.push(bound)
      }
    }
  }
  const lo = Math.min(...lows)
  const hi = Math.max(...highs)
  const rawSpan = hi - lo
  // A flat series (one week, or a constant spec) still needs a readable window.
  const span = rawSpan > 0 ? rawSpan : Math.max(Math.abs(hi) * 0.05, Math.pow(10, -decimals))
  const step = niceStep(span / 4)
  const floor = Math.floor((lo - span * 0.08) / step) * step
  const ceil = Math.ceil((hi + span * 0.08) / step) * step

  const ticks: number[] = []
  for (let v = floor; v <= ceil + step / 2; v += step) {
    ticks.push(Number(v.toFixed(6)))
  }

  const band = plotWidth / data.length
  const xOf = (i: number) => PAD.left + band * i + band / 2
  const yOf = (v: number) => PAD.top + plotHeight - ((v - floor) / (ceil - floor)) * plotHeight

  // Break the line and the band across weeks with no value, so a gap reads as
  // "not measured" and not as a dip to the floor.
  const segments: Segment[][] = []
  let current: Segment[] = []
  data.forEach((d, i) => {
    if (d.avg === null || d.min === null || d.max === null) {
      if (current.length) segments.push(current)
      current = []
    } else {
      current.push({ week: d.week, avg: d.avg, min: d.min, max: d.max, i })
    }
  })
  if (current.length) segments.push(current)

  /** The min–max envelope: along the maxima, back along the minima. */
  function bandPath(segment: Segment[]): string {
    const forward = segment.map(
      (p, n) => `${n === 0 ? 'M' : 'L'}${num(xOf(p.i))},${num(yOf(p.max))}`,
    )
    const back = [...segment].reverse().map((p) => `L${num(xOf(p.i))},${num(yOf(p.min))}`)
    return `${forward.join(' ')} ${back.join(' ')} Z`
  }

  /**
   * A spec limit as a stepwise horizontal line: one segment across each week
   * whose value is known, drawn edge to edge so adjacent weeks with the same
   * limit read as one continuous line and a revision reads as a step. Weeks
   * with a null (mixed or undefined) get no segment at all.
   */
  function limitPath(read: (d: WeekStat) => number | null): string {
    return data
      .map((d, i) => {
        const bound = read(d)
        if (bound === null) return ''
        const y = yOf(bound)
        return `M${num(PAD.left + band * i)},${num(y)} L${num(PAD.left + band * (i + 1))},${num(y)}`
      })
      .filter((part) => part !== '')
      .join(' ')
  }

  const loPath = limitPath((d) => d.lo)
  const hiPath = limitPath((d) => d.hi)
  const tgtPath = limitPath((d) => d.tgt)

  const labelEvery = Math.ceil((data.length * 48) / Math.max(plotWidth, 1))

  const hoveredPoint = hovered === null ? undefined : data[hovered]
  const active =
    hovered === null || hoveredPoint === undefined
      ? null
      : { index: hovered, point: hoveredPoint }

  const selectedIndex = data.findIndex((d) => d.week === selectedWeek)
  const selectedPoint = selectedIndex >= 0 ? data[selectedIndex] : undefined
  const selected =
    selectedPoint === undefined || selectedPoint.avg === null
      ? null
      : { index: selectedIndex, point: selectedPoint, value: selectedPoint.avg }

  // Markers only where they carry meaning: the hovered week, and the selected
  // one when it is not the hovered one.
  const markers: { week: string; x: number; y: number }[] = []
  if (active !== null && active.point.avg !== null) {
    markers.push({ week: active.point.week, x: xOf(active.index), y: yOf(active.point.avg) })
  }
  if (selected !== null && selected.point.week !== active?.point.week) {
    markers.push({ week: selected.point.week, x: xOf(selected.index), y: yOf(selected.value) })
  }

  return (
    <div className="chart-wrap" ref={ref}>
      <svg
        width={chartWidth}
        height={HEIGHT}
        role="img"
        aria-label={valued
          .map((d) => `${weekLabel(d.week)}: avg ${fmtSpec(d.avg, decimals)}`)
          .join(', ')}
      >
        {ticks.map((tick) => (
          <g key={tick}>
            <line
              x1={PAD.left}
              x2={chartWidth - PAD.right}
              y1={yOf(tick)}
              y2={yOf(tick)}
              stroke={tick === ticks[0] ? 'var(--baseline)' : 'var(--gridline)'}
              strokeWidth={1}
            />
            <text
              x={PAD.left - 8}
              y={yOf(tick) + 4}
              textAnchor="end"
              fontSize={11}
              fill="var(--text-muted)"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {tickLabel(tick, decimals)}
            </text>
          </g>
        ))}

        {(loPath !== '' || hiPath !== '') && (
          <g className="spec-limits" aria-hidden>
            {loPath !== '' && (
              <path
                d={loPath}
                fill="none"
                stroke="var(--status-critical)"
                strokeWidth={1}
                strokeDasharray="4 3"
                opacity={0.65}
              />
            )}
            {hiPath !== '' && (
              <path
                d={hiPath}
                fill="none"
                stroke="var(--status-critical)"
                strokeWidth={1}
                strokeDasharray="4 3"
                opacity={0.65}
              />
            )}
          </g>
        )}
        {tgtPath !== '' && (
          <path
            className="spec-target"
            d={tgtPath}
            fill="none"
            stroke="var(--status-good)"
            strokeWidth={1.5}
            strokeDasharray="6 4"
            opacity={0.8}
            aria-hidden
          />
        )}

        {segments.map((segment) => (
          <path
            key={`band-${segment[0]?.week ?? ''}`}
            d={bandPath(segment)}
            fill="var(--series-1)"
            fillOpacity={0.14}
            stroke="none"
          />
        ))}

        {segments.map((segment) => (
          <path
            key={`avg-${segment[0]?.week ?? ''}`}
            d={segment
              .map((p, n) => `${n === 0 ? 'M' : 'L'}${num(xOf(p.i))},${num(yOf(p.avg))}`)
              .join(' ')}
            fill="none"
            stroke="var(--series-1)"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}

        {active !== null && (
          <line
            x1={xOf(active.index)}
            x2={xOf(active.index)}
            y1={PAD.top}
            y2={PAD.top + plotHeight}
            stroke="var(--baseline)"
            strokeWidth={1}
          />
        )}

        {markers.map((m) => (
          <circle
            key={m.week}
            cx={m.x}
            cy={m.y}
            r={4.5}
            fill="var(--series-1)"
            stroke="var(--surface-1)"
            strokeWidth={2}
          />
        ))}

        {data.map(
          (d, i) =>
            d.oos > 0 && (
              <path
                key={`oos-${d.week}`}
                className="oos-marker"
                d={`M${num(xOf(i) - 4)},${num(PAD.top + 2)} L${num(xOf(i) + 4)},${num(
                  PAD.top + 2,
                )} L${num(xOf(i))},${num(PAD.top + 9)} Z`}
                fill="var(--status-critical)"
                aria-label={`${d.oos.toString()} out of spec, week of ${weekLabel(d.week)}`}
              />
            ),
        )}

        {selected !== null && (
          <text
            x={xOf(selected.index)}
            y={yOf(selected.value) - 12}
            textAnchor="middle"
            fontSize={12}
            fontWeight={600}
            fill="var(--text-primary)"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {fmtSpec(selected.value, decimals)}
          </text>
        )}

        {data.map((d, i) => (
          <g key={d.week}>
            <rect
              x={PAD.left + band * i}
              y={PAD.top}
              width={band}
              height={plotHeight}
              fill="transparent"
              className="bar-rect"
              onMouseEnter={() => {
                setHovered(i)
              }}
              onMouseLeave={() => {
                setHovered(null)
              }}
              onClick={() => {
                onSelectWeek(d.week)
              }}
            />
            {(i % labelEvery === 0 || d.week === selectedWeek) && (
              <text
                x={xOf(i)}
                y={HEIGHT - PAD.bottom + 16}
                textAnchor="middle"
                fontSize={11}
                fill={d.week === selectedWeek ? 'var(--text-primary)' : 'var(--text-muted)'}
              >
                {weekShort(d.week)}
              </text>
            )}
            {(i === 0 || (i > 0 && data[i - 1]?.week.slice(0, 4) !== d.week.slice(0, 4))) && (
              <text
                x={xOf(i)}
                y={HEIGHT - PAD.bottom + 29}
                textAnchor="middle"
                fontSize={10}
                fill="var(--text-muted)"
              >
                {d.week.slice(0, 4)}
              </text>
            )}
          </g>
        ))}
      </svg>

      {active !== null && (
        <div
          className="tooltip"
          style={{
            left: xOf(active.index),
            top: active.point.avg !== null ? yOf(active.point.avg) : PAD.top + plotHeight / 2,
          }}
        >
          <div className="tooltip-title">Week of {weekLabel(active.point.week)}</div>
          <div className="tooltip-value">
            {active.point.avg === null
              ? 'Not measured'
              : `avg ${fmtSpec(active.point.avg, decimals)}`}
          </div>
          {active.point.avg !== null && (
            <div className="tooltip-title">
              {`min ${fmtSpec(active.point.min, decimals)} · max ${fmtSpec(
                active.point.max,
                decimals,
              )} · n ${active.point.n.toString()}`}
            </div>
          )}
          {(active.point.lo !== null ||
            active.point.hi !== null ||
            active.point.tgt !== null) && (
            <div className="tooltip-title">
              {[
                active.point.tgt !== null
                  ? `target ${fmtSpec(active.point.tgt, decimals)}`
                  : null,
                active.point.lo !== null || active.point.hi !== null
                  ? `spec ${fmtSpec(active.point.lo, decimals)}–${fmtSpec(
                      active.point.hi,
                      decimals,
                    )}`
                  : null,
              ]
                .filter((part) => part !== null)
                .join(' · ')}
            </div>
          )}
          {active.point.oos > 0 && (
            <div className="tooltip-oos">
              {active.point.oos.toString()} out of spec
            </div>
          )}
        </div>
      )}
    </div>
  )
}
